import React, { useEffect } from 'react';
import {
    CalendarClock,
    RefreshCcw,
    Split,
    Megaphone,
    CameraOff,
    AtSign,
    Compass,
    Workflow,
    Calculator,
    Film,
    Search,
    LayoutDashboard,
    ListChecks,
    PhoneCall,
    Scale,
    CheckCircle2,
    XCircle
} from 'lucide-react';

/* Reusable card: icon + heading + subheading, nothing else */
const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

export default function QalakaarSprint() {

    useEffect(() => {
        document.title = "Qalakaar | Distribution Sprint";
        window.scrollTo(0, 0);
    }, []);

    const sprintSteps = [
        { n: '01', h: 'You fill the brand questionnaire', s: 'Your own copy of the doc is linked at the bottom of this page. About 20 minutes. Your story, your product, who pays you and who performs on your platform.' },
        { n: '02', h: 'We research your niche', s: 'Competitor and content analysis on the same system we built when Razorpay came to us for consulting.' },
        { n: '03', h: 'One strategy call', s: 'You and anyone from your team. We walk the plan together and you pressure-test it live.' },
        { n: '04', h: 'The implementation plan lands', s: 'Within 7 days of the questionnaire. Yours to keep, whoever ends up executing it.' }
    ];

    const wont = [
        { h: 'A retainer commitment today', s: 'The sprint exists so you never have to make that call blind. Step 2 happens only if the plan earns it.' },
        { h: 'Follower counts as the goal', s: 'You said it yourself: conversions matter, vanity metrics do not. The plan is built around revenue and platform supply.' },
        { h: 'A templated deck', s: 'Every recommendation comes from your niche, your baseline and your launch window. Nothing recycled.' }
    ];

    const will = [
        { h: 'The full plan within 7 days', s: 'From the day your questionnaire lands. Fast enough to matter before launch.' },
        { h: 'Rs 9,000 adjusted against month 1', s: 'Move to the done-for-you retainer within 30 days and the sprint costs you nothing.' },
        { h: 'Written for internal sign-off', s: 'Clear enough that your team and CFO can evaluate it without you having to defend it.' }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Qalakaar</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Private</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Confidential · Private Proposal
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        Distribution ready<br />before the product is.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-16 font-light">
                        A one-time strategy sprint for Qalakaar.<br className="hidden sm:block" /> The complete organic distribution plan for both sides of your marketplace, sized so your team can say yes without a leap of faith.
                    </p>

                    <div className="max-w-md mx-auto">
                        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-6 py-8">
                            <div className="text-[10px] sm:text-xs text-white/40 font-bold tracking-widest uppercase mb-3">Investment · One-time</div>
                            <div className="text-4xl sm:text-5xl font-black text-white tracking-tight">₹9,000</div>
                            <div className="text-xs sm:text-sm text-white/50 mt-2">plan in 7 days · adjusts against month 1 if we go ahead</div>
                        </div>
                    </div>
                </section>

                {/* THE SPRINT */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The sprint</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Four steps.<br />Seven days.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You asked for the minimum way to associate before putting a big commercial to your team. This is it, built to be exactly that.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {sprintSteps.map((step) => (
                            <div key={step.n} className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
                                <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{step.n}</div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{step.h}</h3>
                                    <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{step.s}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* PAIN POINTS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The honest read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Where Qalakaar is right now.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={CalendarClock} heading="Launch is months away" subheading="The product lands in 4 to 5 months · the audience it launches to is not built yet" accent="rose" />
                        <InfoCard icon={RefreshCcw} heading="No content pipeline" subheading="Your words on the call: nothing goes out regularly · no system, no one governing it" accent="rose" />
                        <InfoCard icon={Split} heading="Two audiences, one feed" subheading="Artists you need on the platform and B2B buyers who pay · each needs its own message" accent="rose" />
                        <InfoCard icon={Megaphone} heading="Ads without an organic layer" subheading="Meta ads you already run well · without organic trust behind them, every click costs more than it should" accent="rose" />
                        <InfoCard icon={CameraOff} heading="Founder time is the bottleneck" subheading="A pipeline that needs you on camera every week is a pipeline that stops" accent="rose" />
                        <InfoCard icon={AtSign} heading="LinkedIn and X unanswered" subheading="The B2B revenue side lives there · today there is no plan for it" accent="rose" />
                    </div>
                </section>

                {/* WHAT THE SPRINT DELIVERS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">Our side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What the sprint delivers.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Compass} heading="Brand DNA and positioning" subheading="Built from your identity and your own read on live music · nothing templated, no shared visual identity" accent="indigo" />
                        <InfoCard icon={Workflow} heading="The two-sided funnel plan" subheading="Short form that pulls artists to the platform · a separate lane for B2B revenue, LinkedIn and X included" accent="indigo" />
                        <InfoCard icon={Calculator} heading="The gap analysis you asked for" subheading="What you are not doing, what to change, the expected result of each change · written for your team and CFO" accent="indigo" />
                        <InfoCard icon={Film} heading="The content system" subheading="Formats, cadence and the pipeline that runs without you recording every week" accent="indigo" />
                        <InfoCard icon={Search} heading="Competitor intelligence" subheading="Who wins attention in your space and why · the same analysis system built for Razorpay" accent="indigo" />
                        <InfoCard icon={LayoutDashboard} heading="App beta access" subheading="The Meta-verified dashboard and AI setter from our call · included for the sprint, normally retainer-only" accent="indigo" />
                    </div>
                </section>

                {/* YOUR SIDE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">Your side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we need from you.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-4xl mx-auto">
                        <InfoCard icon={ListChecks} heading="20 minutes on the questionnaire" subheading="That is the only homework · everything else is on us" accent="emerald" />
                        <InfoCard icon={PhoneCall} heading="One strategy call" subheading="Bring your team · the plan should survive their questions" accent="emerald" />
                        <InfoCard icon={Scale} heading="Decide with the plan in hand" subheading="Run it in-house or hand it to us · the plan is yours either way" accent="emerald" />
                    </div>
                </section>

                {/* PROMISE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The exchange</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we promise.<br />And what we won't.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        {/* WON'T */}
                        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 sm:p-9">
                            <div className="text-rose-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we won't promise</div>
                            <ul className="space-y-6">
                                {wont.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <XCircle className="w-5 h-5 text-rose-400/70 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/50 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* WILL */}
                        <div className="rounded-3xl border border-indigo-500/30 bg-indigo-900/10 p-7 sm:p-9">
                            <div className="text-indigo-300/90 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we do promise</div>
                            <ul className="space-y-6">
                                {will.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <CheckCircle2 className="w-5 h-5 text-indigo-300 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* STEP 2 */}
                    <div className="max-w-3xl mx-auto mt-10">
                        <div className="rounded-2xl border border-amber-500/20 bg-amber-900/10 p-6 sm:p-8 text-center">
                            <div className="text-amber-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-4">Step 2, only if the plan earns it</div>
                            <p className="text-sm sm:text-base text-white/60 font-light leading-relaxed">
                                The done-for-you engagement at ₹1L to ₹2L per month depending on scope. Ideation, scripting, recording through your AI clone, editing, posting and management across Instagram, YouTube and Facebook, with a minimum guarantee agreed upfront against your goal.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        Ready when you are.
                    </h2>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto font-light leading-relaxed mb-10">
                        Your questionnaire is ready below. This is your own copy, just open and fill it. The moment it lands with us, the 7-day clock starts.
                    </p>
                    <a
                        href="https://docs.google.com/document/d/1xT4Mm2MBkIEoh4jFfl8pZIIRiAeoHjoZboLdGXeW_8U/edit"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        Open your questionnaire
                    </a>
                    <div className="mt-6">
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition text-sm">or write to suresh@socialseo.in</a>
                    </div>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
